import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Box, CircularProgress } from '@material-ui/core'
import { getContrastingColor } from 'react-color/lib/helpers'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import { useGetViewsByTags, useListViewTags, useUpdateViewTagViews } from '../../../../../api/abundanceEngine'
import SydButton from '../../../../commonDesign/Button'
import Alert from '../../../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../../../constants'
import SnackAlert from '../../../../molecules/SnackAlert/SnackAlert'
import NotAvailable from '../../../../organisms/NotAvailable'
import { useCheckPolicy } from '../../../../../hooks'
import TagManagementBox from './TagManagementBox'

function AbundanceEngineManageTags () {
  const { data: viewListByTags, isLoading: isViewListByTagsLoading } = useGetViewsByTags({
    sort: [{ field: 'name', dir: 'asc' }]
  })

  const { data: tagList, isLoading: isTagListLoading } = useListViewTags({
    sort: [{ field: 'name', dir: 'asc' }],
    limit: 10,
    filters: undefined
  })

  const [proposedViewsByTags, setProposedViewsByTags] = useState({})
  const [currentViewsByTags, setCurrentViewsByTags] = useState({})

  const hasChanges = useMemo(() => {
    if (!proposedViewsByTags || !currentViewsByTags) return false
    const tagNames = new Set([...Object.keys(proposedViewsByTags), ...Object.keys(currentViewsByTags)])
    for (const tagName of tagNames) {
      const proposed = proposedViewsByTags[tagName] || []
      const current = currentViewsByTags[tagName] || []
      const proposedIds = proposed.map(v => v.viewId ?? v.value).filter(Boolean).sort()
      const currentIds = current.map(v => v.viewId ?? v.value).filter(Boolean).sort()
      if (proposedIds.length !== currentIds.length) return true
      for (let i = 0; i < proposedIds.length; i++) {
        if (proposedIds[i] !== currentIds[i]) return true
      }
    }
    return false
  }, [proposedViewsByTags, currentViewsByTags])

  const { changesByTag, numProposedChanges } = useMemo(() => {
    const byTag = []
    let total = 0
    if (!proposedViewsByTags || !currentViewsByTags || !tagList) return { changesByTag: byTag, numProposedChanges: 0 }
    const tagNames = new Set([...Object.keys(proposedViewsByTags), ...Object.keys(currentViewsByTags)])
    const tagMap = (tagList || []).reduce((acc, t) => { acc[t.name] = t; return acc }, {})
    for (const tagName of tagNames) {
      const proposed = proposedViewsByTags[tagName] || []
      const current = currentViewsByTags[tagName] || []
      const currentIds = new Set(current.map(v => v.viewId ?? v.value))
      const proposedIds = new Set(proposed.map(v => v.viewId ?? v.value))
      const added = proposed.filter(v => !currentIds.has(v.viewId ?? v.value)).map(v => v.name).sort((a, b) => (a || '').localeCompare(b || ''))
      const removed = current.filter(v => !proposedIds.has(v.viewId ?? v.value)).map(v => v.name).sort((a, b) => (a || '').localeCompare(b || ''))
      if (added.length || removed.length) {
        byTag.push({ tag: tagMap[tagName] || { name: tagName, displayName: tagName, color: '#E0E0E0' }, added, removed })
        total += added.length + removed.length
      }
    }
    return { changesByTag: byTag, numProposedChanges: total }
  }, [proposedViewsByTags, currentViewsByTags, tagList])

  const { mutateAsync: updateViewTagViewsAsync, isLoading: isSubmitting } = useUpdateViewTagViews()

  useEffect(() => {
    if (
      tagList === undefined ||
      tagList.length === 0 ||
      viewListByTags === undefined ||
      viewListByTags.length === 0 ||
      isSubmitting === true
    ) return

    const newProposedViewsByTags = {}

    tagList.forEach(tag => {
      newProposedViewsByTags[tag.name] = viewListByTags
        .filter(view => view.tagName === tag.name)
        .sort((a, b) => a.name.localeCompare(b.name))
    })

    setProposedViewsByTags({ ...newProposedViewsByTags })
    setCurrentViewsByTags({ ...newProposedViewsByTags })
  }, [viewListByTags, tagList, isSubmitting])

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const form = useForm()

  const onResetClicked = () => {
    setProposedViewsByTags(currentViewsByTags)
  }

  const onConfirmClose = () => {
    setConfirmModalOpen(false)
  }

  const [alert, setAlert] = useState({})

  const onSubmit = () => {
    const newKeyValuePairings = []
    Object.entries(proposedViewsByTags).forEach(([key, value]) => {
      value.forEach(view => {
        newKeyValuePairings.push({
          viewTagId: tagList.find(x => x.name === key).viewTagId, // TODO key of proposedViewsByTags should be viewTagId
          viewId: view.viewId
        })
      })
    })

    updateViewTagViewsAsync({ viewTagViews: newKeyValuePairings }).then(() => {
      setAlert({
        alertMessage: 'Updating the view tags was successful!',
        alertSeverity: 'success',
        openAlert: true
      })
    }).catch(error => {
      console.error(error)
      setAlert({
        alertMessage: 'Updating the view tags had an error.',
        alertSeverity: 'error',
        openAlert: true
      })
    }).finally(() => {
      setConfirmModalOpen(false)
    })
  }

  // Do not show component if it doesn't have access
  const hasAccess = useCheckPolicy('internalOnly')

  if (!hasAccess) {
    return <NotAvailable />
  }

  return (
    <div className='p-4 flex flex-col gap-2'>
      {
        isViewListByTagsLoading ||
        isTagListLoading
          ? (
            <div className='flex items-center justify-center p-4'>
              <CircularProgress color='secondary' size={18} />
            </div>
          ) : (
            <form
              {...form}
              onSubmit={form.handleSubmit(onSubmit)}
              onClick={(e) => e.stopPropagation()}
              className='flex flex-col gap-2'
            >
              <Box
                className='flex justify-end items-center flex-wrap gap-2'
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'var(--color-surface-050)',
                  padding: 'var(--padding-sm) 0',
                  marginBottom: 'var(--margin-sm)',
                  borderBottom: 'var(--border-neutral-thin)'
                }}
              >
                {form.formState.errors.root?.message && (
                  <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>
                    {form.formState.errors.root.message}
                  </Alert>
                )}

                <SydButton
                  variant='primary'
                  icon='sync'
                  size='sm'
                  onClick={onResetClicked}
                  disabled={isSubmitting || !hasChanges}
                >
                  {isSubmitting === true ? (
                    <CircularProgress color='secondary' size={18} />
                  ) : (
                    <>Reset Change(s)</>
                  )}
                </SydButton>

                <SydButton
                  variant='primary'
                  icon='check'
                  size='sm'
                  disabled={isSubmitting || !hasChanges}
                  onClick={() => {
                    setConfirmModalOpen(true)
                  }}
                >
                  {isSubmitting === true ? (
                    <CircularProgress color='secondary' size={18} />
                  ) : hasChanges ? (
                    <>Submit {numProposedChanges} Change(s)</>
                  ) : (
                    <>Submit Change(s)</>
                  )}
                </SydButton>
              </Box>
              <Box display='flex' justifyContent='center' width='100%'>
                <Box display='flex' className='flex flex-wrap' sx={{ maxWidth: '100%', gap: 'var(--gap-md)' }}>
                  {
                    tagList.map(tag => (
                      <TagManagementBox
                        key={tag.viewTagId}
                        tag={tag}
                        proposedViewsByTags={proposedViewsByTags}
                        setProposedViewsByTags={setProposedViewsByTags}
                        viewListByTags={viewListByTags}
                        tagList={tagList}
                        isTagListLoading={isTagListLoading}
                      />
                    ))
                  }
                </Box>
              </Box>

              <SydModal
                open={confirmModalOpen}
                onClose={onConfirmClose}
                title='Confirm Change(s)'
                subtitle='Please look over the changes before submitting.'
              >
                <Box display='flex' flexDirection='column'>
                  {changesByTag.map(({ tag, added, removed }) => (
                    <Box key={tag.name}>
                      <div
                        className='rounded text-center text-bold'
                        style={{
                          backgroundColor: tag.color || '#E0E0E0',
                          padding: '2px 6px',
                          color: getContrastingColor(tag.color || '#E0E0E0'),
                          display: 'inline-block',
                          marginBottom: 'var(--margin-xs)'
                        }}
                      >
                        {tag.displayName || tag.name}
                      </div>
                      {added.length > 0 && (
                        <Box sx={{ marginLeft: 'var(--margin-sm)', marginTop: 'var(--margin-xs)' }}>
                          <strong>Added:</strong> {added.join(', ')}
                        </Box>
                      )}
                      {removed.length > 0 && (
                        <Box sx={{ marginLeft: 'var(--margin-sm)', marginTop: 'var(--margin-xs)' }}>
                          <strong>Removed:</strong> {removed.join(', ')}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
                <SydModalActions>
                  <SydButton
                    disabled={isSubmitting}
                    onClick={onConfirmClose}
                  >
                    Cancel
                  </SydButton>
                  <SydButton
                    disabled={isSubmitting}
                    variant='primary'
                    type='submit'
                  >
                    {
                      isSubmitting === true ? (
                        <>
                          <CircularProgress color='secondary' size={18} />
                          {' '}
                          Confirm
                        </>
                      ) : (
                        <>Confirm</>
                      )
                    }
                  </SydButton>
                </SydModalActions>
              </SydModal>

            </form>
          )
      }
      <SnackAlert alert={alert} />

    </div>
  )
}

export default AbundanceEngineManageTags
